/**
 * Feature-Based Audit Tracker Hook
 *
 * A higher-level abstraction over useAuditTracker that uses the Feature Registry
 * to automatically configure audit tracking based on feature definitions.
 *
 * This provides a DRY pattern for tracking audit events - you just specify the
 * feature ID and the hook handles all the configuration.
 *
 * Usage:
 * ```typescript
 * const { createMutationCallbacks, trackFeatureAccess } = useFeatureTracker('settings.location.operations')
 *
 * // For mutations - creates callbacks with pre-configured action, resource, severity
 * const { onSuccess, onError } = createMutationCallbacks({
 *   settingName: 'enableClientsToSignUp',
 *   getOldValue: () => previousValue,
 *   getNewValue: () => newValue,
 * })
 * await mutation({ args, onSuccess, onError })
 *
 * // For page views / feature access
 * trackFeatureAccess()
 * ```
 */

import { useCallback, useMemo } from 'react'
import { useAuditTracker, MutationCallbackOptions, MutationCallbacks } from './useAuditTracker'
import { useAuditTrackerConfig, useAuditTrackerConfigOptional } from './useAuditTrackerConfig'
import { FEATURE_REGISTRY, getFeature, type FeatureDefinition } from '../audit/feature-registry'
import {
  AuditAction,
  AuditResource,
  AuditVisibility,
  AuditSeverity,
} from '@hapana/shared/schemas/audit'

// Re-export types for convenience
export type { FeatureDefinition }
export { FEATURE_REGISTRY, getFeature }

/**
 * Options for feature-based mutation callbacks
 * Omits fields that are auto-configured from the feature registry
 */
export interface FeatureMutationCallbackOptions {
  /** ID of the resource being affected (overrides feature default if needed) */
  resourceId?: string
  /** Site ID (optional, for site-scoped events) */
  siteId?: string
  /** Site name (optional, denormalized) */
  siteName?: string

  // For setting changes
  /** Setting key/name */
  settingName?: string
  /** Human-readable setting name */
  settingDisplayName?: string
  /** Function to get the old value */
  getOldValue?: () => unknown
  /** Function to get the new value */
  getNewValue?: () => unknown

  /** Optional custom summary generator */
  getSummary?: (result: unknown) => string

  /** Change type (overrides default if needed) */
  changeType?: 'create' | 'update' | 'delete'

  /** Override action (if different from feature default) */
  action?: AuditAction
  /** Override resource (if different from feature default) */
  resource?: AuditResource
  /** Override severity (if different from feature default) */
  severity?: AuditSeverity
  /** Override visibility (if different from feature default) */
  visibility?: AuditVisibility

  corporateId?: string
}

export interface FeatureTrackerReturn {
  /** The feature definition from the registry */
  feature: FeatureDefinition | undefined
  /** Whether this is a valid feature ID */
  isValidFeature: boolean
  /**
   * Create mutation callbacks pre-configured from the feature registry
   */
  createMutationCallbacks: <TData = unknown, TError = Error>(
    options?: FeatureMutationCallbackOptions
  ) => MutationCallbacks<TData, TError>
  /**
   * Track a feature access event (for page views, report views, etc.)
   */
  trackFeatureAccess: (additionalProps?: Record<string, unknown>) => void
  /** The underlying audit tracker (for advanced use cases) */
  auditTracker: ReturnType<typeof useAuditTracker>
}

/**
 * Hook for feature-based audit tracking
 *
 * Uses the Feature Registry to automatically configure audit events based on
 * the feature ID. This provides consistent, DRY audit tracking across the app.
 *
 * @param featureId - The feature ID from the registry (e.g., 'settings.location.operations')
 */
export function useFeatureTracker(featureId: string): FeatureTrackerReturn {
  // Get the full context from the provider
  const config = useAuditTrackerConfig()

  // Get the base audit tracker
  const auditTracker = useAuditTracker(config)

  // Look up the feature in the registry
  const feature = useMemo(() => getFeature(featureId), [featureId])

  const isValidFeature = feature !== undefined

  /**
   * Create mutation callbacks with feature-based configuration
   */
  const createMutationCallbacks = useCallback(
    <TData = unknown, TError = Error>(
      options: FeatureMutationCallbackOptions = {}
    ): MutationCallbacks<TData, TError> => {
      if (!feature) {
        console.warn(`[FeatureTracker] Unknown feature ID: ${featureId}`)
        // Return no-op callbacks
        return {
          onSuccess: () => {},
          onError: () => {},
        }
      }

      const {
        action = feature.defaultAction,
        resource = feature.resource,
        severity = feature.defaultSeverity,
        visibility = feature.defaultVisibility,
        resourceId,
        siteId,
        siteName,
        settingName,
        settingDisplayName,
        getOldValue,
        getNewValue,
        getSummary,
        changeType,
        corporateId,
      } = options

      // Merge feature config with provided options
      // Note: PostHog/audit routing is now handled server-side in the Audit Service
      const callbackOptions: MutationCallbackOptions = {
        action,
        resource,
        severity,
        visibility,
        resourceId,
        siteId,
        siteName,
        settingName,
        settingDisplayName,
        getOldValue,
        getNewValue,
        getSummary,
        changeType,
        corporateId,
        featureId: feature.featureId,
        featureCategory: feature.category,
      }

      return auditTracker.createMutationCallbacks<TData, TError>(callbackOptions)
    },
    [feature, featureId, auditTracker]
  )

  /**
   * Track a feature access event
   *
   * Use this for page views, report views, and other non-mutation tracking
   */
  const trackFeatureAccess = useCallback(
    (additionalProps?: Record<string, unknown>) => {
      if (!feature) {
        console.warn(`[FeatureTracker] Unknown feature ID: ${featureId}`)
        return
      }

      // Use the FEATURE_ACCESSED action for access tracking
      auditTracker.trackEvent({
        action: AuditAction.FEATURE_ACCESSED,
        resource: feature.resource,
        visibility: feature.defaultVisibility,
        severity: AuditSeverity.INFO,
        details: {
          summary: `Accessed ${feature.displayName}`,
          metadataJson: JSON.stringify({
            featureId: feature.featureId,
            category: feature.category,
            ...additionalProps,
          }),
        },
      })
    },
    [feature, featureId, auditTracker]
  )

  return {
    feature,
    isValidFeature,
    createMutationCallbacks,
    trackFeatureAccess,
    auditTracker,
  }
}

/**
 * Hook for feature-based tracking that doesn't throw if context is missing
 * Use this for components that may or may not be inside an AuditTrackerConfigProvider
 */
export function useFeatureTrackerOptional(featureId: string): FeatureTrackerReturn | null {
  const config = useAuditTrackerConfigOptional()

  // If no config, return null
  if (!config) {
    return null
  }

  // Get the base audit tracker
  const auditTracker = useAuditTracker(config)

  // Look up the feature in the registry
  const feature = getFeature(featureId)
  const isValidFeature = feature !== undefined

  const createMutationCallbacks = <TData = unknown, TError = Error>(
    options: FeatureMutationCallbackOptions = {}
  ): MutationCallbacks<TData, TError> => {
    if (!feature) {
      return {
        onSuccess: () => {},
        onError: () => {},
      }
    }

    // Note: PostHog/audit routing is now handled server-side in the Audit Service
    return auditTracker.createMutationCallbacks<TData, TError>({
      action: options.action ?? feature.defaultAction,
      resource: options.resource ?? feature.resource,
      severity: options.severity ?? feature.defaultSeverity,
      visibility: options.visibility ?? feature.defaultVisibility,
      ...options,
      featureId: feature.featureId,
      featureCategory: feature.category,
    })
  }

  const trackFeatureAccess = (additionalProps?: Record<string, unknown>) => {
    if (!feature) {
      return
    }

    auditTracker.trackEvent({
      action: AuditAction.FEATURE_ACCESSED,
      resource: feature.resource,
      visibility: feature.defaultVisibility,
      severity: AuditSeverity.INFO,
      details: {
        summary: `Accessed ${feature.displayName}`,
        metadataJson: JSON.stringify({
          featureId: feature.featureId,
          category: feature.category,
          ...additionalProps,
        }),
      },
    })
  }

  return {
    feature,
    isValidFeature,
    createMutationCallbacks,
    trackFeatureAccess,
    auditTracker,
  }
}

export default useFeatureTracker
